import type { Dispatch, FormEvent, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import SchoolYearDropdown from "../../../components/SchoolYearDropdown";
import type {
  Classroom,
  SchoolActivity,
  SchoolTaskType,
  SchoolYear,
} from "../types";

export interface SchoolTaskFormValues {
  name: string;
  description: string;
  type: SchoolTaskType;
  schoolYearId: string;
  classroomIds: string[];
  startsAt: string;
  capacity: number;
  optional: boolean;
  activityId: string;
}

export interface SchoolTaskEditorProps {
  form: SchoolTaskFormValues;
  setForm: Dispatch<SetStateAction<SchoolTaskFormValues>>;
  isEditing: boolean;
  activities: SchoolActivity[];
  classrooms: Classroom[];
  years: SchoolYear[];
  onSubmit: (event: FormEvent) => Promise<void>;
  onCancel: () => void;
  formId?: string;
  hideActions?: boolean;
}

export function SchoolTaskEditor({
  form,
  setForm,
  isEditing,
  activities,
  classrooms,
  years,
  onSubmit,
  onCancel,
  formId,
  hideActions = false,
}: SchoolTaskEditorProps) {
  const { t } = useTranslation();

  return <form id={formId} onSubmit={onSubmit} className="grid gap-x-4 gap-y-5 lg:grid-cols-2">
    <SchoolYearDropdown schoolYears={years} selectedSchoolYearId={form.schoolYearId} onChange={(schoolYearId) => setForm((previous) => ({ ...previous, schoolYearId, activityId: activities.some((activity) => activity.id === previous.activityId && activity.schoolYearId === schoolYearId) ? previous.activityId : "", classroomIds: [] }))} label={t("schoolTasks.schoolYear", { defaultValue: "School year" })} />
    <EditorInput label={t("schoolTasks.name", { defaultValue: "Name" })} required value={form.name} onChange={(name) => setForm((previous) => ({ ...previous, name }))} />
    <label className="grid gap-2 text-sm font-bold lg:col-span-2">{t("schoolTasks.description", { defaultValue: "Description" })}<textarea value={form.description} onChange={(event) => setForm((previous) => ({ ...previous, description: event.target.value }))} className="min-h-28 rounded-xl border border-slate-200 bg-white p-3 font-normal outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900" /></label>
    <label className="grid gap-1 text-sm font-bold">{t("schoolTasks.type", { defaultValue: "Type" })}<select aria-label={t("schoolTasks.type", { defaultValue: "Type" })} value={form.type} onChange={(event) => setForm((previous) => ({ ...previous, type: event.target.value as SchoolTaskType }))} className="rounded-xl border p-3 dark:bg-slate-900"><option value="PARENT_TASK">{t("schoolTasks.types.parent", { defaultValue: "Parent task" })}</option><option value="GENERAL_TASK">{t("schoolTasks.types.general", { defaultValue: "General task" })}</option><option value="HOLIDAY_CLEANING">{t("schoolTasks.types.holiday", { defaultValue: "Holiday cleaning" })}</option></select></label>
    <EditorInput label={t("schoolTasks.dateTime", { defaultValue: "Date and time" })} type="datetime-local" value={form.startsAt} onChange={(startsAt) => setForm((previous) => ({ ...previous, startsAt }))} />
    <label className="grid gap-1 text-sm font-bold">{t("schoolTasks.activity", { defaultValue: "Activity" })}<select aria-label={t("schoolTasks.activity", { defaultValue: "Activity" })} value={form.activityId} onChange={(event) => setForm((previous) => ({ ...previous, activityId: event.target.value }))} className="rounded-xl border p-3 dark:bg-slate-900"><option value="">{t("schoolTasks.withoutActivity", { defaultValue: "Without activity" })}</option>{activities.filter((activity) => activity.schoolYearId === form.schoolYearId).map((activity) => <option key={activity.id} value={activity.id}>{activity.name}</option>)}</select></label>
    <EditorInput label={t("schoolTasks.capacity", { defaultValue: "Capacity" })} type="number" min={1} required value={String(form.capacity)} onChange={(capacity) => setForm((previous) => ({ ...previous, capacity: Number(capacity) }))} />
    {form.type === "PARENT_TASK" && <fieldset className="lg:col-span-2"><legend className="text-sm font-bold">{t("schoolTasks.classrooms", { defaultValue: "Classrooms" })}</legend><div className="mt-2 flex flex-wrap gap-2">{classrooms.filter((classroom) => String(classroom.schoolYear?.id || classroom.schoolYearId) === form.schoolYearId).map((classroom) => <label key={classroom.id} className="rounded-full border px-3 py-2 text-sm"><input type="checkbox" checked={form.classroomIds.includes(classroom.id)} onChange={() => setForm((previous) => ({ ...previous, classroomIds: previous.classroomIds.includes(classroom.id) ? previous.classroomIds.filter((id) => id !== classroom.id) : [...previous.classroomIds, classroom.id] }))} /> <span>{classroom.className}</span></label>)}</div></fieldset>}
    <label className="flex items-center gap-2 text-sm font-bold"><input type="checkbox" checked={form.optional} onChange={(event) => setForm((previous) => ({ ...previous, optional: event.target.checked }))} />{t("schoolTasks.optional", { defaultValue: "Optional" })}</label>
    {!hideActions && <div className="flex gap-2 lg:col-span-2"><button className="rounded-xl bg-indigo-600 px-5 py-2.5 font-bold text-white">{isEditing ? t("common.save", { defaultValue: "Save" }) : t("schoolTasks.addTask", { defaultValue: "Add task" })}</button><button type="button" onClick={onCancel} className="rounded-xl border px-5 py-2.5">{t("common.cancel", { defaultValue: "Cancel" })}</button></div>}
  </form>;
}

function EditorInput({
  label,
  value,
  onChange,
  type = "text",
  required = false,
  min,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
  min?: number;
}) {
  return (
    <label className="grid gap-1 text-sm font-bold">
      {label}
      <input
        required={required}
        min={min}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-xl border p-3 dark:bg-slate-900"
      />
    </label>
  );
}
